/* ═══════════════════════════════════════════════════════════
   Home page: everything the other pages have, plus the
   category grid that grows and the example gardens that scroll.
   ═══════════════════════════════════════════════════════════ */
import './app.js';
import { createSlider } from './slider.js';

/* ═══════════════════════════════════════════════════════════
   "Meer categorieën".

   The band under a category grid fetches the next batch of
   rows and puts it under the ones there are, until the list
   runs out and the band goes (client, 13 Oct 2026). There
   are two grids — the phone's above the three steps, the
   desktop's beside the sidebar — and the page shows one; each
   has its own band and its own place in the list.

   Where to carry on from travels on the last row, the same
   cursor the slider reads (see _tile_rows.php). The answer
   is rows from the same partial, so a row that arrives is
   indistinguishable from one that came with the page.
   ═══════════════════════════════════════════════════════════ */
/* One listener on the document rather than one per band: the desktop grid
   waits in a <template> on a phone and is only put in place when the screen
   reaches `lg` (page_home.php), which can be after this has run — a tablet
   turned on its side. A band that arrives late works like one that came with
   the page. */
document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-more-tiles]');
  const grid = button?.closest('section')?.querySelector('[data-tiles]');

  if (!grid) return;

  const arrow = button.querySelector('[data-arrow]');
  const endpoint = grid.dataset.endpoint || '/';
  const cursor = () => grid.lastElementChild?.dataset.next;

  const asked = cursor();

  if (!asked || button.getAttribute('aria-busy') === 'true') return;

  button.setAttribute('aria-busy', 'true');
  arrow?.classList.add('animate-load-drop');

  try {
    const join = endpoint.includes('?') ? '&' : '?';
    const response = await fetch(`${endpoint}${join}tiles=${encodeURIComponent(asked)}`, {
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(response.status);

    /* Bare <li>s: a parser throws them away unless they are handed a list
       to sit in. */
    const batch = new DOMParser()
      .parseFromString(`<ul>${await response.text()}</ul>`, 'text/html')
      .querySelector('ul');

    const previous = grid.lastElementChild;

    if (batch?.children.length) grid.append(...batch.children);

    /* The row that carried us here is not the end of the list any more —
       and if nothing came back, whatever it promised, it is the end. */
    delete previous.dataset.next;

    if (!cursor()) button.remove();
  } catch {
    /* The cursor is untouched, so the next press asks for the same batch. */
  } finally {
    button.removeAttribute('aria-busy');
    arrow?.classList.remove('animate-load-drop');
  }
});

/* ═══════════════════════════════════════════════════════════
   The example gardens: a row that comes with a first batch and
   asks this page for the next as the visitor moves through.
   Everything it does is in slider.js.
   ═══════════════════════════════════════════════════════════ */

/* ── photographs that wait for the page ──
   The banners after the first and the first batch of example gardens carry
   their addresses in data-defer-srcset and data-defer-src (page_home.php,
   _example_slides.php), so they do not compete with the first banner, the
   largest paint on the page. They come in once the page has loaded, or
   sooner when somebody reaches for one of those rows. */
const wakeImages = () => {
  document.querySelectorAll('[data-defer-srcset]').forEach((element) => {
    element.srcset = element.dataset.deferSrcset;
    element.removeAttribute('data-defer-srcset');
  });

  document.querySelectorAll('[data-defer-src]').forEach((element) => {
    element.src = element.dataset.deferSrc;
    element.removeAttribute('data-defer-src');
  });
};

if (document.readyState === 'complete') {
  wakeImages();
} else {
  addEventListener('load', wakeImages, { once: true });
}

/* A slider whose slides arrive after the page has loaded — the one the screen
   only turns to once it crosses `md` (page_home.php) — brings its own deferred
   addresses, and they are wanted straight away. */
document.addEventListener('slider:filled', () => {
  if (document.readyState === 'complete') wakeImages();
});

document.querySelectorAll('[data-slider] [data-track], [data-examples]').forEach((track) => {
  track.addEventListener('pointerdown', wakeImages, { once: true, passive: true });
  track.addEventListener('scroll', wakeImages, { once: true, passive: true });
});

const examples = document.querySelector('[data-examples]');

createSlider({
  track: examples,
  prev: document.querySelector('[data-examples-prev]'),
  next: document.querySelector('[data-examples-next]'),
  hint: examples?.closest('section')?.querySelector('[data-swipe-hint]'),
  param: 'examples',
});
